import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../../lib/api/auth';

export const ResetPasswordForm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token.');
        }
    }, [token]);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return 'Password must be at least 8 characters long.';
        if (!/\d/.test(pwd)) return 'Password must contain at least one number.';
        if (!/[a-zA-Z]/.test(pwd)) return 'Password must contain at least one letter.';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            setError('Missing reset token.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        const strengthError = validatePassword(password);
        if (strengthError) {
            setError(strengthError);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await authService.resetPassword({ token, newPassword: password });
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to reset password. Token may be expired.');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="w-full max-w-md p-8 glass rounded-2xl shadow-xl animate-fade-in text-center">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="w-8 h-8" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_2bf9f70a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2bf9f70a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Password Reset Successful</h2>
                <p className="text-gray-600 mb-8">
                    Your password has been updated. Redirecting to login...
                </p>
                <Link
                    to="/login"
                    className="btn-primary w-full py-3 rounded-lg text-white font-semibold inline-block"
                >
                    Sign In Now
                </Link>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md p-8 glass rounded-2xl shadow-xl animate-fade-in">
            <div className="mb-8 text-center">
                <h1 className="text-3xl font-medium bg-clip-text text-transparent bg-gradient-to-r from-accent-primary to-orange-600">
                    Reset Password
                </h1>
                <p className="text-neutral-500 mt-2">Enter your new secure password</p>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded animate-slide-in">
                    <p className="font-medium">Error</p>
                    <p className="text-sm">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                        New Password
                    </label>
                    <input
                        type="password"
                        required
                        className="input w-full focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <p className="text-xs text-neutral-500 mt-1">Min 8 chars, letters & numbers required</p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Confirm Password
                    </label>
                    <input
                        type="password"
                        required
                        className="input w-full focus:ring-2 focus:ring-accent-primary focus:border-transparent transition-all"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading || !token}
                    className="w-full btn-primary py-3 rounded-lg text-white font-semibold transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 shadow-lg shadow-accent-primary/20"
                >
                    {isLoading ? (
                        <div className="flex items-center space-x-2 justify-center">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Resetting...</span>
                        </div>
                    ) : (
                        'Reset Password'
                    )}
                </button>
            </form>
        </div>
    );
};
